<script>
  import { fade } from "svelte/transition";
  import { onDestroy } from "svelte";

  export let visible = false;
  export let isMobileOrTablet = false;
  export let contained = false;
  export let onDismiss = () => {};
  export let iconSrc = "images/icon-clickndrag.svg";
  export let message = isMobileOrTablet ? "Swipe to rotate" : "Click & Drag";

  let timeoutId;
  let gestureArmed = false;
  const DISMISS_EVENTS = ["pointerdown", "keydown"];
  const dismissOptions = { capture: true };

  function disarmGestureDismiss() {
    DISMISS_EVENTS.forEach((type) => {
      document.removeEventListener(type, gestureDismissHandler, dismissOptions);
    });
    gestureArmed = false;
  }

  function gestureDismissHandler(e) {
    if (e.type === "pointerdown" && e.button !== 0) return;
    if (e.type === "keydown" && e.key !== "Enter" && e.key !== "Escape") return;
    handleDismiss(e.type === "keydown" ? "enter-key" : "user-pointer");
  }

  /** Listen for engagement without covering the pano (so the same drag rotates). */
  function armGestureDismiss() {
    if (gestureArmed) return;
    DISMISS_EVENTS.forEach((type) => {
      document.addEventListener(type, gestureDismissHandler, dismissOptions);
    });
    gestureArmed = true;
  }

  function handleDismiss(reason = "unknown") {
    console.log("[TutorialOverlay] dismiss requested in component", { reason, visible });
    if (timeoutId) clearTimeout(timeoutId);
    timeoutId = null;
    disarmGestureDismiss();
    onDismiss(reason);
  }

  $: if (visible) {
    console.log("[TutorialOverlay] visible in component", {
      isMobileOrTablet,
      message,
    });
    armGestureDismiss();
    if (timeoutId) clearTimeout(timeoutId);
    timeoutId = setTimeout(() => handleDismiss("auto-timeout"), 5000);
  } else {
    disarmGestureDismiss();
  }

  onDestroy(() => {
    if (timeoutId) clearTimeout(timeoutId);
    disarmGestureDismiss();
  });
</script>

{#if visible}
  <div
    class="tutorial-wrapper"
    class:contained
    role="dialog"
    aria-label={message}
    aria-live="polite"
    transition:fade={{ duration: 500 }}
  >
    <div class="tutorial-card">
      <img
        class="tutorial-icon"
        src={iconSrc}
        alt=""
        width="80"
        height="80"
      />
      <p class="tutorial-text">{message}</p>
    </div>
  </div>
{/if}

<style>
  .tutorial-wrapper {
    position: fixed;
    inset: 0;
    /* iOS: use viewport-relative height so flex centering works (dvh + -webkit-fill-available) */
    height: 100vh;
    height: 100dvh;
    min-height: -webkit-fill-available;
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 100;
    /* Pass pointer events through so the first drag reaches the pano. */
    pointer-events: none;
  }

  .tutorial-wrapper.contained {
    position: absolute;
    height: auto;
    min-height: 0;
    z-index: 5;
  }

  .tutorial-card {
    width: 150px;
    padding: 1.5rem 1.25rem;
    border-radius: 24px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 1rem;
    background: rgba(35, 31, 31, 0.8);
    box-shadow: 0 0 6px rgba(0, 0, 0, 0.8);
    /* Card stays clickable; document capture listener still dismisses. */
    pointer-events: auto;
    cursor: pointer;
  }

  .tutorial-icon {
    object-fit: contain;
    flex-shrink: 0;
  }

  .tutorial-text {
    color: #ffffff;
    font-size: 1.25rem;
    font-family: var(--font-body, inherit), sans-serif;
    text-align: center;
    margin: 0;
  }
</style>
